import { useEffect, useMemo, useState } from 'react';
import { Document, DocumentStatus } from '../../types/document.ts';
import { Search, Plus, Download, Eye, Edit, Filter, Send, X, Trash2, UserCircle, Users, GitBranch } from 'lucide-react';
import { useRedirect } from 'react-admin';
import axios from 'axios';
import { submitDocumentForApproval } from '../../services/documentService.ts';
import { getAllTemplates, getStepsByTemplateId } from '../../services/workflowService.ts';
import { getAllUsers } from '../../services/userService.ts';

interface Approver {
  id: string;
  name: string;
  role: string;
  email: string;
}

interface ApprovalStep {
  id: string;
  stepNumber: number;
  stepName: string;
  stepType: 'single' | 'parallel' | 'conditional';
  approvers: Approver[];
  approvalRule: 'all' | 'any' | 'majority';
  timeLimit?: number;
  description: string;
  isOptional: boolean;
}

// Map backend numeric status to string status
const mapStatusToString = (status: number | string): DocumentStatus => {
  if (typeof status === 'string') return status as DocumentStatus;
  const statusMap: Record<number, DocumentStatus> = {
    0: 'Draft',
    1: 'Review',
    2: 'Approved',
    3: 'Obsolete'
  };
  return statusMap[status] || 'Draft';
};

export default function DocumentManageList() {
  const redirect = useRedirect(); // Initialize redirect function

  // Define navigation functions
  const onNewDocument = () => {
    // First parameter is 'create', second parameter must match the Resource name property, which is 'documentsList'
    redirect('create', 'documentsList');
  };

  const onEditDocument = (id: string) => {
    // First parameter is 'edit', second parameter must match the Resource name property, which is 'documentsList'
    redirect('edit', 'documentsList', id);
  };


const onViewDocument = (id: string) => {
  redirect('show', 'documentsList', id);
};

  


  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | 'All'>('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [approvalSteps, setApprovalSteps] = useState<ApprovalStep[]>([]);
  const [approversPool, setApproversPool] = useState<Approver[]>([]);
  const [userNameMap, setUserNameMap] = useState<Record<string, string>>({});
  const [showApproverModal, setShowApproverModal] = useState(false);
  const [currentStepId, setCurrentStepId] = useState<string | null>(null);

  const BASE_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
  const API_URL = `${BASE_API_URL}/QmsDocument`;

  useEffect(() => {
    const fetchDocuments = async () => {
      setLoading(true);
      setError(null);
      try {
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize - 1;
        const range = `[${start},${end}]`;
        const filter: Record<string, string> = {};

        if (categoryFilter !== 'All') {
          filter.category = categoryFilter;
        }

        if (statusFilter !== 'All') {
          filter.status = statusFilter;
        }

        const response = await axios.get<any>(API_URL, {
          params: {
            range,
            perPage: pageSize,
            filter: Object.keys(filter).length ? JSON.stringify(filter) : undefined,
          },
        });
        // Map numeric status to string status
        const documentsWithMappedStatus = (response.data || []).map((doc: any) => ({
          ...doc,
          status: mapStatusToString(doc.status)
        }));
        setDocuments(documentsWithMappedStatus);
        
        // Get total count from response headers if available
        const contentRange = response.headers['content-range'];
        if (contentRange) {
          const match = contentRange.match(/\d+-\d+\/(\d+)/);
          if (match) {
            setTotalCount(parseInt(match[1], 10));
          }
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load documents, please try again later');
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [API_URL, categoryFilter, statusFilter, currentPage, pageSize]);

  // Load approval templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const allTemplates = await getAllTemplates();
        setTemplates(allTemplates);
      } catch (error) {
        console.error('Failed to load templates:', error);
      }
    };
    loadTemplates();
  }, []);

  // Load approvers
  useEffect(() => {
    const loadApprovers = async () => {
      try {
        const users = await getAllUsers();
        const approvers: Approver[] = users.map((user, index) => ({
          id: user.id?.toString() || `u${index + 1}`,
          name: user.userName || '',
          role: user.roleName || 'User',
          email: user.email || '',
        }));
        setApproversPool(approvers);
        
        // Create user name map (userId -> userName)
        const nameMap: Record<string, string> = {};
        users.forEach((user) => {
          if (user.id && user.userName) {
            nameMap[user.id.toString()] = user.userName;
          }
        });
        setUserNameMap(nameMap);
      } catch (error) {
        console.error('Failed to load approvers:', error);
      }
    };
    loadApprovers();
  }, []);

  // Load approval steps when template changes in submit modal
  useEffect(() => {
    if (selectedTemplateId && showSubmitModal) {
      const loadTemplateSteps = async () => {
        try {
          const steps = await getStepsByTemplateId(selectedTemplateId);
          const transformedSteps: ApprovalStep[] = steps.map((step: any) => ({
            id: `s${step.stepNo || step.stepNumber}`,
            stepNumber: step.stepNo || step.stepNumber,
            stepName: step.name || step.stepName || `Step ${step.stepNo || step.stepNumber}`,
            stepType: step.stepType || 'single',
            description: step.description || '',
            timeLimit: step.timeLimit,
            isOptional: step.isOptional || false,
            approvalRule: step.approvalRule || 'all',
            approvers: step.approvers ? step.approvers
              .map((approver: any) => {
                // Backend returns approver objects with userId and userName
                const userId = (approver.userId || approver.id)?.toString();
                const foundApprover = approversPool.find(a => a.id === userId);
                if (foundApprover) return foundApprover;
                // Create approver from backend data
                return {
                  id: userId || '',
                  name: approver.userName || approver.name || `User ${userId}`,
                  role: approver.role || approver.roleName || 'User',
                  email: approver.email || '',
                };
              })
              .filter((a: Approver | undefined): a is Approver => a !== undefined) : []
          }));
          setApprovalSteps(transformedSteps);
        } catch (error) {
          console.error('Failed to load template steps:', error);
        }
      };
      loadTemplateSteps();
    }
  }, [selectedTemplateId, showSubmitModal, approversPool]);

  // Helper function to get display name for updated by
  const getDisplayName = (updatedBy: string, createdBy: string): string => {
    // If updatedBy is not empty, try to get the name from the map or return the value itself
    if (updatedBy && updatedBy.trim()) {
      return userNameMap[updatedBy] || updatedBy;
    }
    // If updatedBy is empty, use createdBy
    if (createdBy && createdBy.trim()) {
      return userNameMap[createdBy] || createdBy;
    }
    return 'Unknown';
  };

  const categories = useMemo(() => {
    const cats = new Set(documents.map(doc => doc.category));
    return ['All', ...Array.from(cats)];
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      const matchesSearch =
        doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.documentNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

      return matchesSearch;
    });
  }, [documents, searchQuery]);

  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const getStatusColor = (status: DocumentStatus) => {
    switch (status) {
      case 'Draft':
        return 'bg-gray-100 text-gray-700';
      case 'Review':
        return 'bg-yellow-100 text-yellow-700';
      case 'Approved':
        return 'bg-green-100 text-green-700';
      case 'Obsolete':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const handleDownload = (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation();
    const activeVersion = doc.versions.find(v => v.isActive);
    if (activeVersion?.fileUrl) {
      alert(`Download file: ${doc.documentNumber} v${activeVersion.version}\nFile: ${activeVersion.fileUrl}`);
    }
  };

  const handleSubmitForApproval = async (docId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDocId(docId);
    setSelectedTemplateId(undefined);
    setApprovalSteps([]);
    setShowSubmitModal(true);
    
    // Load document's template if exists
    const doc = documents.find(d => d.id === docId);
    const docWithTemplate = doc as (Document & { templateId?: number }) | undefined;
    if (docWithTemplate && docWithTemplate.templateId) {
      try {
        const steps = await getStepsByTemplateId(docWithTemplate.templateId.toString());
        const transformedSteps: ApprovalStep[] = (steps || []).map((step: any) => ({
          id: step.id?.toString() || '',
          stepNumber: step.stepNo || step.stepNumber || 0,
          stepName: step.name || step.stepName || '',
          stepType: step.stepType || 'single',
          approvers: (step.approvers || []).map((approver: any) => {
            // Backend returns approver objects with userId and userName
            const userId = (approver.userId || approver.id)?.toString();
            const foundApprover = approversPool.find(a => a.id === userId);
            if (foundApprover) return foundApprover;
            return {
              id: userId || '',
              name: approver.userName || approver.name || `User ${userId}`,
              role: approver.role || approver.roleName || 'User',
              email: approver.email || '',
            };
          }),
          approvalRule: step.approvalRule || 'all',
          timeLimit: step.timeLimit || 1,
          description: step.description || '',
          isOptional: step.isOptional === true,
        }));
        setApprovalSteps(transformedSteps);
        setSelectedTemplateId(docWithTemplate.templateId);
      } catch (error) {
        console.error('Failed to load template steps:', error);
      }
    }
  };

  const handleAddApprover = (stepId: string, approver: Approver) => {
    setApprovalSteps(steps =>
      steps.map(s =>
        s.id === stepId ? { ...s, approvers: [...s.approvers, approver] } : s
      )
    );
  };

  const handleRemoveApprover = (stepId: string, approverId: string) => {
    setApprovalSteps(steps =>
      steps.map(s =>
        s.id === stepId ? { ...s, approvers: s.approvers.filter(a => a.id !== approverId) } : s
      )
    );
  };

  const handleAddCustomStep = () => {
    const newStep: ApprovalStep = {
      id: `custom-${Date.now()}`,
      stepNumber: approvalSteps.length + 1,
      stepName: `Custom Step ${approvalSteps.length + 1}`,
      stepType: 'single',
      approvers: [],
      approvalRule: 'all',
      timeLimit: 2,
      description: '',
      isOptional: false
    };
    setApprovalSteps([...approvalSteps, newStep]);
  };

  const handleRemoveStep = (stepId: string) => {
    const updatedSteps = approvalSteps
      .filter(s => s.id !== stepId)
      .map((s, idx) => ({ ...s, stepNumber: idx + 1 }));
    setApprovalSteps(updatedSteps);
  };

  const getStepTypeBadge = (type: string) => {
    const configs: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
      single: {
        label: 'Single',
        color: 'bg-blue-100 text-blue-800',
        icon: <UserCircle className="size-3" />
      },
      parallel: {
        label: 'Parallel',
        color: 'bg-purple-100 text-purple-800',
        icon: <Users className="size-3" />
      },
      conditional: {
        label: 'Conditional',
        color: 'bg-orange-100 text-orange-800',
        icon: <GitBranch className="size-3" />
      }
    };
    const config = configs[type] || configs.single;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const handleConfirmSubmit = async () => {
    if (!selectedDocId) return;

    setSubmitting(true);
    try {
      const result = await submitDocumentForApproval(
        selectedDocId,
        selectedTemplateId,
        approvalSteps.length > 0 ? approvalSteps : undefined
      );
      
      alert(`Document submitted successfully!\nWorkflow Instance ID: ${result.id}\nCurrent Step: ${result.currentStepNo}`);
      setShowSubmitModal(false);
      setSelectedDocId(null);
      setApprovalSteps([]);
      setSelectedTemplateId(undefined);
      
      // Refresh document list without redirect
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/QmsDocument`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Map numeric status to string status
      const documentsWithMappedStatus = (response.data || []).map((doc: any) => ({
        ...doc,
        status: mapStatusToString(doc.status)
      }));
      setDocuments(documentsWithMappedStatus);
    } catch (error: any) {
      console.error('Failed to submit document:', error);
      alert(`Failed to submit document: ${error.message || 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full h-full overflow-auto flex flex-col">
      {/* Header and Filters */}
      <div className="px-4 py-4 flex-shrink-0 border-b border-gray-200 bg-white sticky top-0 z-10">
        {/* Title and Button */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Document List</h2>
          <button
            onClick={onNewDocument}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            Create New Document
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by document number, title, description or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-gray-600 text-sm">Filter:</span>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as DocumentStatus | 'All')}
              className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="All">All Status</option>
              <option value="Draft">Draft</option>
              <option value="Review">In Review</option>
              <option value="Approved">Approved</option>
              <option value="Obsolete">Obsolete</option>
            </select>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'All' ? 'All Categories' : cat}
                </option>
              ))}
            </select>

            <div className="ml-auto text-gray-600 text-sm">
              {loading ? 'Loading...' : `${totalCount} documents`}
            </div>
          </div>
        </div>
      </div>

      {/* Documents Table - scrollable */}
      <div className="flex-1 overflow-auto bg-white border-b border-gray-200">
        <div className="min-h-full">
          {error && (
            <div className="px-6 py-4 text-red-600">{error}</div>
          )}

          <table className="w-full border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-2 py-2 text-left text-gray-600 font-medium whitespace-nowrap text-sm min-w-[80px]">Document Number</th>
                <th className="px-2 py-2 text-left text-gray-600 font-medium text-sm min-w-[120px]">Title</th>
                <th className="px-2 py-2 text-left text-gray-600 font-medium whitespace-nowrap text-sm min-w-[80px]">Category</th>
                <th className="px-2 py-2 text-center text-gray-600 font-medium whitespace-nowrap text-sm min-w-[60px]">Version</th>
                <th className="px-2 py-2 text-center text-gray-600 font-medium whitespace-nowrap text-sm min-w-[70px]">Status</th>
                <th className="px-2 py-2 text-center text-gray-600 font-medium whitespace-nowrap text-sm min-w-[80px]">UpdatedAt</th>
                <th className="px-2 py-2 text-left text-gray-600 font-medium whitespace-nowrap text-sm min-w-[80px]">UpdatedBy</th>
                <th className="px-2 py-2 text-center text-gray-600 font-medium whitespace-nowrap text-sm min-w-[110px]">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {filteredDocuments.map(doc => (
                <tr
                  key={doc.id}
                  className="hover:bg-gray-50 transition-colors cursor-pointer text-sm"
                  onClick={() => onViewDocument(doc.id)}
                >
                  {/* Document Number */}
                  <td className="px-2 py-2 text-blue-600 font-medium whitespace-nowrap text-sm">{doc.documentNumber}</td>

                  {/* Title */}
                  <td className="px-2 py-2">
                    <div className="text-gray-900 font-medium truncate text-sm">{doc.title}</div>
                    <div className="text-gray-500 text-xs truncate">{doc.description}</div>
                  </td>

                  {/* Category */}
                  <td className="px-2 py-2">
                    <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs whitespace-nowrap">
                      {doc.category}
                    </span>
                  </td>

                  {/* Version */}
                  <td className="px-2 py-2 text-center text-gray-900 italic whitespace-nowrap text-sm">v{doc.currentVersion}</td>

                  {/* Status */}
                  <td className="px-2 py-2 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded font-base text-xs whitespace-nowrap ${getStatusColor(doc.status)}`}>
                      {doc.status === 'Draft' && 'Draft'}
                      {doc.status === 'Review' && 'In Review'}
                      {doc.status === 'Approved' && 'Approved'}
                      {doc.status === 'Obsolete' && 'Obsolete'}
                    </span>
                  </td>

                  {/* Updated Date */}
                  <td className="px-2 py-2 text-center text-gray-600 whitespace-nowrap text-xs">
                    {new Date(doc.updatedAt).toLocaleDateString('en-US')}
                  </td>

                  {/* Updated By */}
                  <td className="px-2 py-2 text-gray-600 whitespace-nowrap text-sm">{getDisplayName(doc.updatedBy, doc.createdBy)}</td>

                  {/* Actions */}
                  <td className="px-2 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={(e) => { e.stopPropagation(); onViewDocument(doc.id); }}
                        className="inline-flex items-center gap-0.5 px-1.5 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="View"
                      >
                        <Eye className="w-3 h-3" />
                      </button>
                      {doc.status !== 'Obsolete' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onEditDocument(doc.id); }}
                          className="inline-flex items-center gap-0.5 px-1.5 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        onClick={(e) => handleSubmitForApproval(doc.id, e)}
                        className="inline-flex items-center gap-0.5 px-1.5 py-1 text-xs text-green-600 hover:bg-green-50 rounded transition-colors"
                        title="Submit"
                      >
                        <Send className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => handleDownload(doc, e)}
                        className="inline-flex items-center gap-0.5 px-1.5 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        title="Download"
                      >
                        <Download className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Empty state message */}
          {filteredDocuments.length === 0 && (
            <div className="py-12 text-center text-gray-500">
              No documents found
            </div>
          )}

          {/* Pagination */}
          {documents.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <span>Rows per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(parseInt(e.target.value, 10));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <span>
                  {totalCount === 0 ? 'No data' : `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, totalCount)} of ${totalCount}`}
                </span>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || loading}
                  className="p-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Previous Page"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <div className="flex items-center h-8 px-2 mx-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => {
                    // Show page numbers with smart logic: show 5 pages around current
                    const showPages = Math.min(5, totalPages);
                    const startPage = Math.max(1, Math.min(currentPage - Math.floor(showPages / 2), totalPages - showPages + 1));
                    const endPage = Math.min(totalPages, startPage + showPages - 1);
                    
                    if (pageNum < startPage || pageNum > endPage) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`min-w-8 h-8 px-0.5 rounded text-sm transition-colors ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages || loading}
                  className="p-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Next Page"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Submit for Approval Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Submit for Approval</h3>
                <button
                  onClick={() => setShowSubmitModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={submitting}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Template Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Approval Template
                </label>
                <select
                  value={selectedTemplateId || ''}
                  onChange={(e) => setSelectedTemplateId(e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={submitting}
                >
                  <option value="">-- Use default template --</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name || template.templateName} ({template.docType || template.documentType})
                    </option>
                  ))}
                </select>
                {selectedTemplateId && (
                  <p className="text-xs text-gray-500 mt-1">
                    Template loaded. You can adjust approvers as needed.
                  </p>
                )}
              </div>

              {/* Approval Steps */}
              {approvalSteps.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">
                      Approval Steps ({approvalSteps.length})
                    </label>
                    <button
                      type="button"
                      onClick={handleAddCustomStep}
                      disabled={submitting}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="size-4" />
                      Add Custom Step
                    </button>
                  </div>

                  <div className="space-y-3">
                    {approvalSteps.map((step, index) => (
                      <div key={step.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                            {step.stepNumber}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium text-gray-900">{step.stepName}</h4>
                              {getStepTypeBadge(step.stepType)}
                              {step.isOptional && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                                  Optional
                                </span>
                              )}
                              {step.timeLimit && (
                                <span className="text-xs text-gray-500">
                                  {step.timeLimit} day limit
                                </span>
                              )}
                            </div>
                            {step.description && (
                              <p className="text-sm text-gray-600 mb-3">{step.description}</p>
                            )}
                            
                            {/* Approvers */}
                            <div className="mb-2">
                              <div className="flex flex-wrap gap-2">
                                {step.approvers.map(approver => (
                                  <div
                                    key={approver.id}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm"
                                  >
                                    <UserCircle className="size-4 text-gray-600" />
                                    <div>
                                      <div className="font-medium text-gray-900">{approver.name}</div>
                                      <div className="text-xs text-gray-500">{approver.role}</div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveApprover(step.id, approver.id)}
                                      disabled={submitting}
                                      className="ml-1 text-gray-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <X className="size-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCurrentStepId(step.id);
                                    setShowApproverModal(true);
                                  }}
                                  disabled={submitting}
                                  className="inline-flex items-center gap-2 px-3 py-1.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-white hover:border-blue-500 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Plus className="size-4" />
                                  Add Approver
                                </button>
                              </div>
                              {!step.isOptional && step.approvers.length === 0 && (
                                <p className="text-xs text-red-600 mt-2">* At least one approver must be assigned to this required step</p>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveStep(step.id)}
                            disabled={submitting}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Trash2 className="size-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {approvalSteps.length === 0 && (
                <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                  <GitBranch className="size-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-2">No approval workflow configured yet</p>
                  <p className="text-sm text-gray-500 mb-4">
                    Select an approval template or add custom steps
                  </p>
                  <button
                    type="button"
                    onClick={handleAddCustomStep}
                    disabled={submitting}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="size-4" />
                    Add First Step
                  </button>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                type="button"
                onClick={() => setShowSubmitModal(false)}
                disabled={submitting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                disabled={submitting}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approver Selection Modal */}
      {showApproverModal && currentStepId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Select Approver</h3>
                <button
                  onClick={() => {
                    setShowApproverModal(false);
                    setCurrentStepId(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="size-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
                {approversPool.map(approver => {
                  const currentStep = approvalSteps.find(s => s.id === currentStepId);
                  const isAlreadyAdded = currentStep?.approvers.some(a => a.id === approver.id);
                  
                  return (
                    <button
                      key={approver.id}
                      type="button"
                      onClick={() => {
                        if (!isAlreadyAdded && currentStepId) {
                          handleAddApprover(currentStepId, approver);
                        }
                      }}
                      disabled={isAlreadyAdded}
                      className={`p-4 border rounded-lg text-left transition-colors ${
                        isAlreadyAdded
                          ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-50'
                          : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <UserCircle className="size-10 text-gray-400" />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{approver.name}</div>
                          <div className="text-sm text-gray-600">{approver.role}</div>
                          <div className="text-xs text-gray-500">{approver.email}</div>
                        </div>
                        {isAlreadyAdded && (
                          <div className="text-green-600 text-xs">Added</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowApproverModal(false);
                  setCurrentStepId(null);
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
